import {
  Box,
  Flex,
  Heading,
  Typography,
} from "@sidelinesports/fan-design-system";
import { LockOutlineIcon } from "@sidelinesports/icons";
import { Link } from "react-router";
import type { ApiScheduledGame, ParticipatedGame } from "../../../../api";
import { ListItem, UploadedImage } from "../../../../components";
import { GAME_STATUS_LABELS } from "../../../../constants/status-labels";
import { getGameShortTitle } from "../../../../utils";
import { canUserPlayGame } from "../../rules";
import styles from "./GameListItem.module.css";
import { GameTimestamp } from "./GameTimestamp";
import { Tag } from "./Tag";

export type GameListItemProps = {
  game: ApiScheduledGame;
  participatedGame?: ParticipatedGame;
};

export const GameListItem = ({ game, participatedGame }: GameListItemProps) => {
  const isDisabled = !canUserPlayGame(
    game,
    participatedGame ? [participatedGame] : [],
  );

  if (isDisabled) {
    return <DisabledGameListItem game={game} />;
  }

  return <EnabledGameListItem game={game} />;
};

const EnabledGameListItem = ({ game }: { game: ApiScheduledGame }) => {
  return (
    <ListItem clickable bg="secondary">
      <GameLink game={game}>
        <Flex
          className={styles["game-list-item"]}
          gap={16}
          justify="center"
          align="center"
        >
          <GameImage game={game} />
          <GameTitle game={game} />
          <Tag>{GAME_STATUS_LABELS[game.status]}</Tag>
        </Flex>
      </GameLink>
    </ListItem>
  );
};

const DisabledGameListItem = ({ game }: { game: ApiScheduledGame }) => {
  return (
    <ListItem clickable bg="primary" aria-disabled="true">
      <Flex
        className={styles["game-list-item"]}
        gap={16}
        justify="center"
        align="center"
        aria-disabled="true"
      >
        <GameImage game={game} />
        <GameTitle game={game} />
        <Tag
          style={
            {
              "--border-color": "transparent",
            } as React.CSSProperties
          }
        >
          <LockOutlineIcon />
          {GAME_STATUS_LABELS[game.status]}
        </Tag>
      </Flex>
    </ListItem>
  );
};

const GameLink = ({
  children,
  game,
}: {
  children: React.ReactElement;
  game: ApiScheduledGame;
}) => {
  return (
    <Box
      className={styles["game-link"]}
      as={Link}
      to={`/games/${game.id}`}
      w="100%"
      h="100%"
    >
      {children}
    </Box>
  );
};

const GameTitle = ({ game }: { game: ApiScheduledGame }) => {
  return (
    <Box w="100%">
      <Heading level={3} textAlign="left" style={{ fontWeight: 600 }}>
        {getGameShortTitle(game)}
      </Heading>
      <Typography size="md">
        <GameTimestamp game={game} />
      </Typography>
    </Box>
  );
};

const GameImage = ({ game }: { game: ApiScheduledGame }) => {
  const { image, alt } = getGameImage(game);

  if (!image) {
    return null;
  }

  return (
    <UploadedImage
      alt={alt}
      image={image}
      w="2.5rem"
      h="2.5rem"
      style={{ flexShrink: 0 }}
      tracking={{ enabled: true, location: "GameListItem::GameImage" }}
    />
  );
};

function getGameImage(game: ApiScheduledGame) {
  if (game.typeEvent) {
    return { image: game.typeEvent.image, alt: game.typeEvent.name };
  }

  if (game.typeMatchup) {
    return {
      image: game.typeMatchup.opponent.logo,
      alt: `${game.typeMatchup.opponent.name} logo`,
    };
  }

  return {};
}
